import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { db } from "../../firebase";

function formatDeadline(deadline) {
  const date = getDeadlineDate(deadline, false);

  if (!date) return null;

  return date.toLocaleDateString("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function getDeadlineDate(deadline, endOfDay = true) {
  if (!deadline) return null;

  let date = null;
  if (typeof deadline?.toDate === "function") {
    date = deadline.toDate();
  } else if (deadline instanceof Date) {
    date = deadline;
  } else if (typeof deadline === "string") {
    const dateOnly = deadline.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
      if (endOfDay) {
        date.setHours(23, 59, 59, 999);
      }
    } else {
      date = new Date(deadline);
    }
  } else if (typeof deadline?.seconds === "number") {
    date = new Date(deadline.seconds * 1000);
  }

  if (!date || Number.isNaN(date.getTime())) return null;

  return date;
}

function isDeadlineExpired(deadline) {
  const deadlineDate = getDeadlineDate(deadline);
  return !!deadlineDate && deadlineDate < new Date();
}

export default function PromoCodeBanner() {
  const location = useLocation();
  const promoCode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("promo_code")?.trim() || null;
  }, [location.search]);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchPromoBanner() {
      if (!promoCode) {
        setBanner(null);
        return;
      }

      try {
        const promoBannerDoc = await getDoc(doc(db, "options", "validPromoCodes", "promoBanners", promoCode));

        if (!isMounted) return;

        if (!promoBannerDoc.exists()) {
          setBanner(null);
          return;
        }

        const promoBanner = promoBannerDoc.data();

        if (promoBanner.active !== true || isDeadlineExpired(promoBanner.deadline)) {
          setBanner(null);
          return;
        }

        setBanner(promoBanner);
      } catch (error) {
        console.error("Unable to load promo banner", error);
        if (isMounted) {
          setBanner(null);
        }
      }
    }

    fetchPromoBanner();

    return () => {
      isMounted = false;
    };
  }, [promoCode]);

  if (!promoCode || !banner) return null;

  const deadline = formatDeadline(banner.deadline);

  return (
    <div className="w-[calc(100%-1.5rem)] max-w-md mx-auto mb-4 rounded-md px-4 py-3 text-center shadow-md" style={{ backgroundColor: "#eef8ef" }}>
      <p className="m-0 inline-flex rounded-full border px-3 py-1 text-sm font-bold text-white" style={{ borderColor: "#114273", backgroundColor: "#114273" }}>
        {banner.title || `${promoCode} applied`}
      </p>
      {banner.message && <p className="m-0 mt-2 text-sm text-slate-700">{banner.message}</p>}
      {deadline && <p className="m-0 mt-2 text-xs font-semibold text-slate-600">Offer ends {deadline}</p>}
    </div>
  );
}
